import { useEffect } from "react";
import { useParams } from "react-router";
import { makeStyles } from "@mui/styles";

import { ForecastListContainer } from "./ForecastListContainer";
import { SidePanel } from "./SidePanel";
import { Forecast } from "../components/Forecast";
import { useDispatch, useSelector } from "react-redux";
import { addList, addPetroNinjaList, selectLists } from "../reducers/listSlice";
import { selectUser } from "../reducers/userSlice";
import { selectProjects } from "../reducers/projectSlice";
import { colors } from "../helpers/colors";
import { formatUwi } from "../helpers/formatUwi";
import { addWells, makeWellCurrent, selectWells, updateWellStatus } from "../reducers/wellSlice";

import { useSocket } from "../context/useSocket";
import { selectTypeCurves } from "../reducers/typeCurveSlice";
import { selectOptions } from "../reducers/optionsSlice";
import { Typography } from "@mui/material";

const KEY_DOWN = 40;
const KEY_UP = 38;
const KEY_F9 = 120;
const KEY_F10 = 121;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    border: 0,
    color: 'text.primary',
    height: '100vh',
    width: 'calc(100% - 60px)',
    paddingTop: 30,
    marginLeft: 60,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
}));


export const ForecastContainer = () => {
  
  const classes = useStyles();
  const dispatch = useDispatch();
  const params = useParams();

  const { socket } = useSocket();

  const options = useSelector(selectOptions);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];
  const projectId = projects?.byId?.[projects?.currentProjectId]?.id;
  const lists = useSelector(selectLists);
  const list = lists?.byId[lists?.currentListId];
  const user = useSelector(selectUser);
  const wells = useSelector(selectWells);
  const typeCurves = useSelector(selectTypeCurves);
  const typeCurve = typeCurves?.byId[list?.typeCurveId];


  // SIDE EFFECTS
  useEffect(() => { // process Petro Ninja list id if provided
    const listId = params?.listId || "";
    if (listId?.length > 0 && projectId?.length > 0 && user?.value?.subscription === 'SSD') {
      console.log('Adding Petro Ninja list id ', listId);
      dispatch(addPetroNinjaList({user: user?.value?.email, projectId: projectId, listId: listId})).then(res => {
        const list = res?.payload?.list;
        const uwiArray = list?.wellUwis;
        let wells = {};
        for (let uwi of uwiArray) {
          const color = colors[Math.floor(Math.random() * colors.length - 1)];
          wells[uwi] = {
            uwi,
            formattedUwi: formatUwi(uwi),
            color,
            lastModified: new Date().toLocaleString(),
            status: 'idle'
          };
        };

        dispatch(addList({projectId: (projectId || "default"), list: list}));
        dispatch(addWells({uwis: uwiArray, wells: wells, listId: listId}));

      });
    };
  },[projectId]); // eslint-disable-line

  if (document.title !== ('Forecast | SSD')) document.title = ('Forecast | SSD');

  if (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess) {
    return (
      <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Typography>Please add subscription to your account</Typography>
      </div>
    );
  } else {
    return (
      <div className={classes.root} onKeyDown={handleKeyDown} tabIndex={0}>
        <ForecastListContainer />
        <SidePanel />
        <Forecast />
      </div>
    );
  };

  function handleKeyDown(event) {
    try {
      let newCurrentUwi;

      if (event.keyCode === KEY_DOWN) {
        list?.wellUwis?.forEach((uwi, i) => {
          const nextValue = list?.wellUwis?.[i+1];
          if (uwi === wells?.currentWellUwi && nextValue) {
            newCurrentUwi = nextValue;
          };
        });
        if (newCurrentUwi) {
          dispatch(makeWellCurrent({listId: list?.id, wellUwi: newCurrentUwi}));
        };
      } else if (event.keyCode === KEY_UP) {
        list?.wellUwis?.forEach((uwi, i) => {
          const nextValue = list?.wellUwis?.[i-1];
          if (uwi === wells?.currentWellUwi && nextValue) {
            newCurrentUwi = nextValue;
          };
        });
        if (newCurrentUwi) {
          dispatch(makeWellCurrent({listId: list?.id, wellUwi: newCurrentUwi}));
        };
      } else if (event.keyCode === KEY_F9) { // re-fit the current well
        const currentUwi = wells?.currentWellUwi;
        if (currentUwi && currentUwi.length > 0) {
          const currentWell = wells?.byUwi?.[currentUwi];
          dispatch(updateWellStatus({uwi: currentUwi, status: 'processing'}));
          sendProcessRequest([currentUwi], currentWell?.params, true);
        };
      } else if (event.keyCode === KEY_F10) { // process the current well
        const currentUwi = wells?.currentWellUwi;
        if (currentUwi && currentUwi.length > 0) {
          const currentWell = wells?.byUwi?.[currentUwi];
          dispatch(updateWellStatus({uwi: currentUwi, status: 'processing'}));
          sendProcessRequest([currentUwi], currentWell?.params, false);
        };
      };
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function sendProcessRequest(uwis, params, reFit) {
    try {
      let localParams = [];
      currentProject?.wells?.forEach(w => {
        let newWell = {...w};
        let wellParams = {...newWell.params};
        const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
        
        for (let fluid of wellFluids) {
          wellParams[fluid] = {...wellParams[fluid], outliers: []};
        };
        localParams.push({...newWell, params: wellParams});
      });
      if (socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: 'process', 
          payload: uwis || [], 
          userEmail: user?.value?.email, 
          params,
          localParams,
          reFit,
          assistedForecast: options?.assistedForecast, 
          tcParams: typeCurve?.params
        }));
      };
    } catch (error) {
      console.log(error);
    };
  };
};
